/**
 * DataRepository — the ONLY contract UI code depends on for data access.
 *
 * Implementations:
 *   - SupabaseRepository  (services/supabase)  → production
 *   - DemoRepository      (services/demo)      → in-memory sample data when Supabase isn't configured
 *
 * Adding a REST/GraphQL backend later = one more implementation of this interface.
 */
import type {
  AppNotification,
  FloatedBy,
  TenderCategory,
  TenderingMethod,
  AppSettings,
  AppUser,
  Amendment,
  AmendmentInput,
  AlertChannel,
  AlertDeliveryStatus,
  TenderAlertEvent,
  TextChannelStatus,
  AuditLog,
  Department,
  FinancialTransaction,
  FinancialTransactionInput,
  Location,
  QuantityTransaction,
  QuantityTransactionInput,
  QuantityTransactionPatch,
  TenderLineItem,
  TenderLineItemInput,
  TenderLineItemPatch,
  TenderDocument,
  TenderInput,
  OrderType,
  ApprovingAuthority,
  BudgetHead,
  GlAccount,
  MonthlyEstimate,
  MonthlyEstimateInput,
  BudgetStatus,
  TenderStatus,
  TenderSummary,
  UserRole,
  UUID,
  Vendor,
  VendorSummary,
  DocumentStatus,
  DocumentType,
  FinancialTxnType,
} from '@/types/domain';

// ---------------------------------------------------------------- Query primitives
export type SortDirection = 'asc' | 'desc';

export interface PageRequest {
  page: number; // 1-based
  pageSize: number;
}

export interface SortRequest<F extends string = string> {
  field: F;
  direction: SortDirection;
}

export interface Paged<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}

export type TenderSearchField = 'all' | 'gemId' | 'tenderNumber' | 'vendor' | 'poNumber' | 'contractNumber';

export interface TenderFilters {
  search?: string;
  searchField?: TenderSearchField;
  statuses?: TenderStatus[];
  departmentId?: UUID;
  locationId?: UUID;
  vendorId?: UUID;
  startFrom?: string;
  startTo?: string;
  endFrom?: string;
  endTo?: string;
  valueMin?: number;
  valueMax?: number;
  utilizationMin?: number;
  utilizationMax?: number;
  /** Tender Type */
  tenderingMethod?: TenderingMethod;
  tenderCategory?: TenderCategory;
  floatedBy?: FloatedBy;
  /** Only tenders whose CRFQ Number / Tender Type / Category / Floated By is still empty. */
  detailsMissing?: boolean;
  orderType?: OrderType;
  /** Repeat orders of one tender. */
  repeatOfTenderId?: UUID;
  /** Additional-budget tag. 'open' = Needed or Requested ("Needs additional budget"). */
  budget?: BudgetStatus | 'open';
  /** 'none' = approving authority not entered. */
  approvingAuthorityId?: UUID | 'none';
  /** Budget head, or 'none' for tenders without one. */
  budgetHeadId?: UUID | 'none';
}

export type TenderSortField =
  | 'gemId'
  | 'title'
  | 'vendorName'
  | 'departmentName'
  | 'contractValue'
  | 'startDate'
  | 'endDate'
  | 'utilizationPct'
  | 'status'
  | 'daysRemaining'
  | 'billProcessedTill'
  | 'updatedAt';

export interface TenderQuery extends TenderFilters, Partial<PageRequest> {
  sort?: SortRequest<TenderSortField>;
}

export interface LedgerQuery extends Partial<PageRequest> {
  tenderId?: UUID;
  search?: string;
  from?: string;
  to?: string;
}

export interface FinancialQuery extends LedgerQuery {
  txnType?: FinancialTxnType;
}

/** 'received' = any uploaded file (uploaded, or verified before verification was removed). */
export type DocumentStatusFilter = DocumentStatus | 'received';

export interface DocumentQuery extends LedgerQuery {
  status?: DocumentStatusFilter;
  docType?: DocumentType;
}

export interface AuditQuery extends LedgerQuery {
  entityType?: string;
  action?: AuditLog['action'];
}

export interface GlobalSearchResult {
  tenders: Pick<TenderSummary, 'id' | 'gemId' | 'tenderNumber' | 'title' | 'vendorName' | 'status'>[];
  vendors: Pick<Vendor, 'id' | 'name' | 'vendorCode'>[];
}

export interface DashboardSummary {
  totals: {
    tenders: number;
    active: number;
    expiringSoon: number;
    expired: number;
    contractValue: number;
    quantity: number;
    consumed: number;
    remaining: number;
  };
  statusDistribution: { status: TenderStatus; count: number }[];
  valueByMonth: { month: string; value: number; count: number }[]; // month = 'YYYY-MM' of award/start
  utilizationBands: { band: string; count: number }[];
  vendorContractCounts: { vendorId: UUID; vendorName: string; count: number; value: number }[];
  expiriesByMonth: { month: string; count: number; value: number }[];
  upcomingExpiries: TenderSummary[];
  /** Tenders by Tender Type / Category / Floated By, plus how many still need those details. */
  classification: {
    byMethod: { key: TenderingMethod | 'missing'; count: number; value: number }[];
    byCategory: { key: TenderCategory | 'missing'; count: number; value: number }[];
    byFloatedBy: { key: FloatedBy | 'missing'; count: number; value: number }[];
    detailsMissing: number;
    byOrderType: { key: OrderType; count: number; value: number }[];
    /** By approving authority; key 'missing' = not entered. */
    byApprover: { key: string; label: string; count: number; value: number }[];
  };
  /** Tenders tagged Needed or Requested for additional budget. */
  additionalBudget: {
    count: number;
    needed: number;
    requested: number;
    amountSought: number;
    /** Largest amounts sought first. */
    top: TenderSummary[];
    /** Running contracts with no tag yet whose paid amount is ≥ 90% of the contract value. */
    suggested: number;
  };
  recentActivity: AuditLog[];
  pendingDocuments: (TenderDocument & { gemId: string; tenderTitle: string })[];
}

export type PendingDocumentRow = TenderDocument & { gemId: string; tenderTitle: string; vendorName?: string | null };
export type QuantityLedgerRow = QuantityTransaction & { gemId: string; tenderTitle: string; unitOfMeasure: string };
export type FinancialLedgerRow = FinancialTransaction & { gemId: string; tenderTitle: string; vendorName?: string | null };

export interface CloseContractInput {
  outcome: 'completed' | 'terminated';
  closedOn: string; // YYYY-MM-DD
  remarks: string;
}

export type PendingAmendmentRow = Amendment & { gemId: string; tenderTitle: string; vendorName?: string | null };

export interface AlertRunResult {
  created: number;
  /** Owner alerts (consumption / expiry) raised by this run. */
  ownerAlerts?: number;
  emailed: number;
  emailError?: string | null;
}

export interface AlertQuery extends Partial<PageRequest> {
  tenderId?: UUID;
  /** Only events having at least one delivery in this status. */
  status?: AlertDeliveryStatus;
  channel?: AlertChannel;
  search?: string;
}

/** Raw rows for the dashboard trend analysis (computed client-side). */
export interface ConsumptionDataset {
  tenders: TenderSummary[];
  transactions: { tenderId: UUID; txnDate: string; txnType: QuantityTransaction['txnType']; quantity: number }[];
}

export interface NewUserInput {
  email: string;
  fullName: string;
  /** Temporary password the administrator shares with the person. */
  password: string;
  role: UserRole;
  phone?: string | null;
  designation?: string | null;
  departmentId?: UUID | null;
  mustChangePassword: boolean;
}

export interface UserContactPatch {
  fullName?: string;
  designation?: string | null;
  phone?: string | null;
}

export type LookupKind = 'departments' | 'locations' | 'approving_authorities' | 'gl_accounts' | 'budget_heads';

/** Master-data row as managed on the Master Data screen. */
export interface LookupRow {
  id: UUID;
  code: string;
  name: string;
  /** Locations only. */
  state?: string | null;
  /** Departments only: the Department Head. */
  headUserId?: UUID | null;
  headName?: string | null;
  isActive: boolean;
  /** Number of tenders referencing this row (GL accounts: tenders + line items). */
  usageCount: number;
}

export interface LookupInput {
  id?: UUID;
  code: string;
  name: string;
  state?: string | null;
  /** Departments only. */
  headUserId?: UUID | null;
  isActive: boolean;
}

/** Department Head's (or manager's / admin's) decision on a requested additional budget. */
export interface BudgetDecisionInput {
  status: 'approved' | 'rejected';
  decisionDate: string;
  remarks?: string | null;
}

export interface DataRepository {
  readonly mode: 'supabase' | 'demo';

  tenders: {
    list(query: TenderQuery): Promise<Paged<TenderSummary>>;
    getById(id: UUID): Promise<TenderSummary | null>;
    getByGemId(gemId: string): Promise<TenderSummary | null>;
    /** UX pre-check; the DB unique index is the authoritative guard. */
    isGemIdAvailable(gemId: string, excludeId?: UUID): Promise<boolean>;
    /** CRFQ numbers are unique, not case-sensitive. */
    isCrfqAvailable(crfq: string, excludeId?: UUID): Promise<boolean>;
    create(input: TenderInput): Promise<TenderSummary>;
    update(id: UUID, patch: Partial<TenderInput>): Promise<TenderSummary>;
    /** Close a contract (managers/admins). */
    close(id: UUID, input: CloseContractInput): Promise<TenderSummary>;
    /** Permanently delete a tender and everything linked to it (admins only). */
    remove(id: UUID): Promise<void>;
    /** Approve / reject a requested additional budget (managers, admins, the department head — also when a Viewer). */
    decideBudget(id: UUID, input: BudgetDecisionInput): Promise<TenderSummary>;
  };

  vendors: {
    list(): Promise<VendorSummary[]>;
    getById(id: UUID): Promise<VendorSummary | null>;
    create(input: Omit<Vendor, 'id' | 'createdAt'>): Promise<Vendor>;
    update(id: UUID, patch: Partial<Omit<Vendor, 'id' | 'createdAt'>>): Promise<Vendor>;
    /** Delete; fails with IN_USE if any tender references the vendor. */
    remove(id: UUID): Promise<void>;
  };

  lookups: {
    /** Active rows only — for dropdowns in forms and filters. */
    departments(): Promise<Department[]>;
    locations(): Promise<Location[]>;
    /** Active approving authorities, in display order. Empty until the database update is run. */
    approvingAuthorities(): Promise<ApprovingAuthority[]>;
    /** Active GL accounts / budget heads. Empty until the database update is run. */
    glAccounts(): Promise<GlAccount[]>;
    budgetHeads(): Promise<BudgetHead[]>;
    /** All rows (incl. inactive) with the number of tenders using each — for the admin screen. */
    listAll(kind: LookupKind): Promise<LookupRow[]>;
    /** Insert (no id) or update (with id). Codes are unique per kind. */
    save(kind: LookupKind, row: LookupInput): Promise<LookupRow>;
    /** Delete; fails with VALIDATION if any tender still uses the row. */
    remove(kind: LookupKind, id: UUID): Promise<void>;
  };

  quantity: {
    listByTender(tenderId: UUID): Promise<QuantityTransaction[]>;
    ledger(query: LedgerQuery): Promise<Paged<QuantityLedgerRow>>;
    create(input: QuantityTransactionInput): Promise<QuantityTransaction>;
    /** Correct an existing record in place (same id). Totals are recalculated from the records. */
    update(id: UUID, patch: QuantityTransactionPatch): Promise<QuantityTransaction>;
    /** Permanently delete a record (managers & administrators). */
    remove(id: UUID): Promise<void>;
  };

  financials: {
    listByTender(tenderId: UUID): Promise<FinancialTransaction[]>;
    ledger(query: FinancialQuery): Promise<Paged<FinancialLedgerRow>>;
    create(input: FinancialTransactionInput): Promise<FinancialTransaction>;
    /** Correct an existing entry in place. */
    update(id: UUID, patch: Omit<FinancialTransactionInput, 'tenderId'>): Promise<FinancialTransaction>;
    /** Delete an entry (managers & administrators; paid entries are protected). */
    remove(id: UUID): Promise<void>;
  };

  lineItems: {
    listByTender(tenderId: UUID): Promise<TenderLineItem[]>;
    create(input: TenderLineItemInput): Promise<TenderLineItem>;
    update(id: UUID, patch: TenderLineItemPatch): Promise<TenderLineItem>;
    /** Delete an item that has no usage (managers & administrators). */
    remove(id: UUID): Promise<void>;
    /** Turn a tender that already has usage into a line-item tender (one item from its current quantity/value). */
    convertTender(tenderId: UUID, description?: string): Promise<void>;
  };

  estimates: {
    /** Monthly estimates of one tender, by month. Empty until the database update is run. */
    listByTender(tenderId: UUID): Promise<MonthlyEstimate[]>;
    /** Estimates of all tenders for months from..to (inclusive, 'YYYY-MM-01'). */
    listRange(from: string, to: string): Promise<MonthlyEstimate[]>;
    /** Upsert the given months; a month with amount null is removed. Returns the tender's estimates. */
    save(tenderId: UUID, rows: MonthlyEstimateInput[]): Promise<MonthlyEstimate[]>;
  };

  documents: {
    listByTender(tenderId: UUID): Promise<TenderDocument[]>;
    list(query: DocumentQuery): Promise<Paged<PendingDocumentRow>>;
    upload(tenderId: UUID, file: File, meta: { docType: DocumentType; title: string; documentId?: UUID }): Promise<TenderDocument>;
    getDownloadUrl(doc: TenderDocument): Promise<string | null>;
    /** File contents for in-app preview (null when not available, e.g. demo mode). */
    getFileBlob(doc: TenderDocument): Promise<Blob | null>;
    /** Verify or reject an uploaded document (managers/admins). */
    review(id: UUID, decision: 'verified' | 'rejected', remarks?: string | null): Promise<void>;
  };

  amendments: {
    listByTender(tenderId: UUID): Promise<Amendment[]>;
    create(input: AmendmentInput): Promise<Amendment>;
    /** Amendments waiting for approval, across all tenders. */
    listPending(): Promise<PendingAmendmentRow[]>;
    /** Approve (applies the change to the contract) or reject (managers/admins). */
    review(id: UUID, decision: 'approved' | 'rejected', remarks?: string | null): Promise<void>;
  };

  audit: {
    list(query: AuditQuery): Promise<Paged<AuditLog>>;
  };

  notifications: {
    listMine(): Promise<AppNotification[]>;
    markRead(id: UUID): Promise<void>;
    markAllRead(): Promise<void>;
    /** Runs the daily job now: creates in-app alerts, then emails admins (if enabled). */
    runChecks(): Promise<AlertRunResult>;
  };

  email: {
    /** Whether the chosen email service is connected (Brevo key, or Gmail relay address + secret). Secrets are never returned. */
    keyConfigured(): Promise<boolean>;
    /** Sends a test email to the signed-in administrator. */
    sendTest(): Promise<{ requestId: number | null; to: string }>;
    /** The email service's answer to a test email (pending until it arrives). `ok` / `detail` come from the database's rule. */
    result(requestId: number): Promise<{ pending: boolean; statusCode?: number; content?: string | null; error?: string | null; ok?: boolean; detail?: string | null }>;
  };

  users: {
    list(): Promise<AppUser[]>;
    updateRole(id: UUID, role: UserRole): Promise<void>;
    setActive(id: UUID, active: boolean): Promise<void>;
    /** Name / designation / phone (admins for anyone; users for themselves). */
    updateContact(id: UUID, patch: UserContactPatch): Promise<void>;
    /** Administrators: create a sign-in + profile directly from the app. Returns the new user id. */
    create(input: NewUserInput): Promise<UUID>;
    /** Administrators: set a new temporary password for another user. */
    resetPassword(id: UUID, password: string, mustChangePassword: boolean): Promise<void>;
    /** Number of tenders this user owns (must be handed over before deleting). */
    ownedTenderCount(id: UUID): Promise<number>;
    /** Permanently delete a user; their tenders go to `transferTo`. Named administrators only. */
    remove(id: UUID, transferTo?: UUID | null): Promise<{ tendersTransferred: number }>;
  };

  alerts: {
    /** Owner alert history: newest first, with per-channel delivery status. */
    list(query: AlertQuery): Promise<Paged<TenderAlertEvent>>;
    /** Re-send a failed / not-configured email or text delivery (managers/admins). Returns the new status. */
    retry(deliveryId: UUID): Promise<AlertDeliveryStatus>;
    /** Which text channels (SMS / WhatsApp) have a provider connected. */
    textChannels(): Promise<TextChannelStatus>;
  };

  analytics: {
    consumptionDataset(): Promise<ConsumptionDataset>;
  };

  settings: {
    get(): Promise<AppSettings>;
    update(patch: Partial<AppSettings>): Promise<AppSettings>;
  };

  dashboard: {
    summary(): Promise<DashboardSummary>;
  };

  backup: {
    /** Every row of every business table, one entry per table (for the Excel backup). */
    exportAll(): Promise<{ name: string; rows: Record<string, unknown>[] }[]>;
  };

  search: {
    global(term: string): Promise<GlobalSearchResult>;
  };
}

/** Thrown for business-rule violations so UI can show a precise message. */
export class DomainError extends Error {
  constructor(
    public code: 'DUPLICATE_GEM_ID' | 'DUPLICATE_CRFQ' | 'NOT_FOUND' | 'VALIDATION' | 'IN_USE' | 'FORBIDDEN' | 'UNKNOWN',
    message: string,
  ) {
    super(message);
    this.name = 'DomainError';
  }
}
